import React, { memo, useContext, useMemo, useCallback, Fragment } from 'react';
import { Checkbox, IconButton, Tooltip, CircularProgress } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { EditOutlined as EditIcon, LaunchTwoTone as LaunchIcon } from '@material-ui/icons';
import { ADD_PRODUCT_INFO, NOT_AVAILABLE, PRODUCTS_COLUMNS, BOOLEAN, MODAL_KEY, PRODUCT_TO_BE_CHANGED, DENY, DOSESPOT_JUMP_START_LOADER } from '../../../constants/patientChart.constant';
import { PatientChartContext } from '../../../contexts/patientChart';
import { CommonPortalContext } from '../../../contexts/commonPortal';
import { ORDERS } from '../../../constants/visits.constant';
import { get as TableGetter, RowTypeConstant, HeaderCellTypeConstant } from '../../subComponents/table';
import { FEATURE } from '../../../utils/feature';
import { isCancelledOrMedicallyDenied } from '../../../utils/productStatus';
import { AddProduct } from '../AddProduct/v1';
import { POPUP_TYPE } from '../../subComponents/popup';
import { IPopup } from '../../../constants/commonPortal.constant';

const hasFeature = FEATURE();

export const useLoaderStyles = makeStyles((theme) => ({
  root: {
    position: 'relative',
  },
  default: {
    color: 'var(--primary-v2)',
  },
}));

export const Products = memo(({
  data = {
    orderId: 0,
    productCost: {},
    productInfo: {},
    addProductInfo: ADD_PRODUCT_INFO(),
    changeProducts: {},
    isAssignedDoctorViewing: false,
    orders: ORDERS(),
    hasActions: false,
  },
  action = {
    getProductCost: async () => {},
    resetProductCost: async () => {},
    addProduct: async () => {},
    getDoseSpot: async (requestBody = {}) => {},
  },
}) => {
  const Table = TableGetter(1);
  const {
    sortBy,
    loading,
    selectedProducts,
    isConfiguredAlternateRx,
    setSelectedProducts,
    setDeny,
    setProductToBeChanged,
    stopLoading,
    startLoading,
    setJumpStart,
    handleModalOpen,
  } = useContext(PatientChartContext);
  const { handler: { handleSnackbarClose, handleSnackbarOpen } } = useContext(CommonPortalContext);
  const {
    orderId = 0,
    productInfo = {},
    changeProducts = {},
    isAssignedDoctorViewing = false,
    orders = ORDERS(),
    hasActions = false,
  } = data;
  /**
   * @type {{
   *    api: {
   *      called: boolean,
   *      loading: boolean,
   *      data: object,
   *    };
   * }}
   */
  const {
    api: productInfoAPI = {},
    Product: products = [],
  } = productInfo;
  const loaderClasses = useLoaderStyles();

  const isOnlyOneProductSelected = useMemo(() => {
    const _selectedProducts = new Set(selectedProducts);

    if (products.length === 1) {
      return true;
    }

    if (_selectedProducts.size === 1) {
      return true;
    }

    return false;
  }, [products, selectedProducts]);

  const handleRowClick = useCallback((event) => {
  }, []);

  const handleDenyProduct = useCallback((product = {}) => {
    setDeny({ ...DENY(), product });
    handleModalOpen({
      key: MODAL_KEY.DENY,
      modal: {},
    });
  }, []);

  const handleChangeProduct = useCallback((product = {}) => {
    setProductToBeChanged({ ...PRODUCT_TO_BE_CHANGED(), product });
    handleModalOpen({
      key: MODAL_KEY.CHANGE_PRODUCT,
      modal: {},
    });
  }, []);

  const handleRowChecked = useCallback((product = {}, _, value = false) => {
    const _selectedProducts = new Set(selectedProducts);
    /**
     * @description CRW-2456 | Deny Product when a product is unchecked.
     */
    if (!hasFeature.DENY_PRODUCT) {
      _selectedProducts.delete(product.key);
    }

    if (hasFeature.DENY_PRODUCT && !value) {
      handleDenyProduct(product);
    }

    if (value) {
      _selectedProducts.add(product.key);
    }

    setSelectedProducts(_selectedProducts);
  }, [selectedProducts, handleDenyProduct]);

  const handleJumpStart = useCallback(async (product = {}) => {
    const loadingTarget = `${DOSESPOT_JUMP_START_LOADER}-pid-${product.ProductId}`;
    const _loading = startLoading(loading, loadingTarget);

    const { status, data } = await action.getDoseSpot({ OrderId: orderId, ProductId: product.ProductId });
    if (status !== 200) {
      handleSnackbarOpen(IPopup({
        open: true,
        title: 'Error',
        type: POPUP_TYPE.ERROR,
        message: 'Failed to open JumpStart window, please try later.',
        onOk: handleSnackbarClose,
      }));
      stopLoading(_loading, loadingTarget);
      return {};
    }

    if (data?.Data?.StringValue?.length) {
      setJumpStart({
        productId: product.ProductId,
        url: data.Data.StringValue,
        orderId,
      });
      handleModalOpen({
        key: MODAL_KEY.DOSESPOT_JUMPSTART,
        modal: {},
      });
    }

    stopLoading(_loading, loadingTarget);
    return {};
  }, [orderId, loading, startLoading, stopLoading]);

  const columns = useMemo(() => {
    if (hasActions) {
      return PRODUCTS_COLUMNS;
    }

    return PRODUCTS_COLUMNS.filter((column) => !column.action);
  }, [hasActions]);

  const rows = useMemo(() => {
    return products.map((product) => {
      const key = `${product.ProductId}-${product.displayTitle}-${product.Qty}`;

      const isCancelled = isCancelledOrMedicallyDenied({ status: product.Status });

      const isAlternateRx = isConfiguredAlternateRx({
        pId: product.ProductId,
        isAlternateRx: Boolean(product?.IsAlternateRx),
        skipCheckingChangeProducts: true,
        skipCheckingAppSettings: false,
      });

      const disableEdit = (() => {
        if (!hasFeature.CHANGE_PRODUCT) {
          return true;
        }

        if (isCancelled) {
          return true;
        }

        if (product.IsAlternateRx) {
          return true;
        }

        if (product.IsLabProduct) {
          return true;
        }

        if (!isAssignedDoctorViewing) {
          return true;
        }

        if (!orders.all.collection.has(orderId)) {
          return true;
        }

        return false;
      })();

      const disableCheckbox = (() => {
        if (isCancelled) {
          return true;
        }

        if (isAlternateRx) {
          return true;
        }

        if (isOnlyOneProductSelected && selectedProducts.has(key)) {
          return true;
        }

        if (!isAssignedDoctorViewing) {
          return true;
        }

        if (!orders.all.collection.has(orderId)) {
          return true;
        }

        return false;
      })();

      const disableJumpStart = (() => {
        if (!hasFeature.DOSESPOT_JUMP_START) {
          return true;
        }

        if (isCancelled) {
          return true;
        }

        if (!isAssignedDoctorViewing) {
          return true;
        }

        return false;
      })();

      const _product = {
        ...product,
        key,
        type: RowTypeConstant.bolderCondensed,
        CheckBox: (
          <Checkbox
            title="product-selection"
            className='checkbox'
            inputProps={{ 'aria-label': 'primary checkbox' }}
            checked={selectedProducts.has(key)}
            disabled={disableCheckbox}
            onChange={handleRowChecked.bind(this, { key, ...product })}
          />
        ),
        DoseSpotJumpStart: <Fragment />,
        EditIcon: (
          <IconButton
            size='small'
            className='primary-button'
            disabled={disableEdit}
            onClick={handleChangeProduct.bind(this, { key, ...product })}
          >
            <EditIcon fontSize='small' />
          </IconButton>
        ),
        OrderType: product.IsLabProduct ? 'Lab' : (product.OrderType || NOT_AVAILABLE),
        autoRefill: (BOOLEAN[product.AutRefill] || BOOLEAN.false),
        shipping: (product.Shipping || NOT_AVAILABLE),
        status: product.IsLabProduct ? product.LabStatus : (product.Status || NOT_AVAILABLE),
        pharmacy: product.IsLabProduct ? '' : (product.Pharmacy || NOT_AVAILABLE),
      };

      const DoseSpotJumpStartComponent = new Map([
        [true, () => (
          <div className='flex flex-column flex-fill justify-content-center'>
            <CircularProgress className={loaderClasses.default} size={18} thickness={4} />
          </div>
        )],
        [false, () => (
          <Tooltip title='DoseSpot Jumpstart' placement='top' arrow>
            <IconButton
              aria-label='dosespot-jumpstart'
              size='small'
              className='primary-button'
              disabled={disableJumpStart}
              onClick={handleJumpStart.bind(this, { key, ...product })}
            >
              <LaunchIcon fontSize='small' />
            </IconButton>
          </Tooltip>
        )],
      ]);

      const loadingTarget = `${DOSESPOT_JUMP_START_LOADER}-pid-${product.ProductId}`;
      const isLoadingJumpStart = loading.has(loadingTarget);

      const DoseSpotJumpStart = DoseSpotJumpStartComponent.get(isLoadingJumpStart);

      if (isAlternateRx) {
        _product.DoseSpotJumpStart = <DoseSpotJumpStart />;
      }

      return _product;
    });
  }, [
    products,
    orders,
    loading,
    isAssignedDoctorViewing,
    selectedProducts,
    changeProducts,
    isOnlyOneProductSelected,
    isConfiguredAlternateRx,
    handleRowChecked,
    handleChangeProduct,
    handleJumpStart,
  ]);

  const AddProductComponent = new Map([
    ['true-true', (<AddProduct data={{ ...data, version: 2 }} action={action} />)],
    ['true-false', <></>],
    ['false-true', <></>],
    ['false-false', <></>],
  ]);

  const isLoading = useMemo(() => {
    if (!productInfoAPI?.called) {
      return true;
    }
    return productInfoAPI?.loading;
  }, [productInfoAPI]);

  return (
    <div className='relative'>
      <Table
        columns={columns}
        rows={rows}
        filterBy=''
        sortBy={sortBy}
        loading={isLoading}
        handler={{ onRowClick: handleRowClick }}
        headerVersion={HeaderCellTypeConstant.v2}
      />
      {/* we can replace the hardcoded false with hasFeature.ADD_PRODUCT to enable the feature */}
      {AddProductComponent.get(`${false}-${changeProducts.api.data.size > 0}`)}
    </div>
  );
});

export default Products;
